import { useState, useMemo, useEffect } from "react";
import { useOrdreStore } from "@store/ordreStore";
import { useAtelierStore } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { navigateTo } from "context/navigation.service";
import type { Ordre } from "types/ordre";

// ─── Helpers ─────────────────────────────────────────────

const RATING_LABELS: Record<number, string> = {
  1: "Mauvais",
  2: "Décevant",
  3: "Correct",
  4: "Très bien",
  5: "Excellent",
};

function Stars({ rating, size = 12 }: { rating: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <svg key={s} width={size} height={size} viewBox="0 0 24 24" fill={s <= rating ? "#5C6BFF" : "#E5E5E5"}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

function RatingBar({ label, count, total, rating }: { label: string; count: number; total: number; rating: number }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex flex-row items-center gap-3">
      <span className="text-h3 text-neutral-400 w-6 text-right">{rating}</span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="#5C6BFF">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
      <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
        <div className="h-full rounded-full bg-[#5C6BFF] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-h3 text-neutral-400 w-8">{count}</span>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────

type FilterTab = "all" | "positive" | "negative";

export function Avis() {
  const orgData = useDataStore((s) => s.orgData);
  const ordres = useOrdreStore((s) => s.ordres);
  const fetchOrdres = useOrdreStore((s) => s.fetchOrdres);
  const [filter, setFilter] = useState<FilterTab>("all");
  const orgMembers = useDataStore((s) => s.orgMembers);
  const atelierJobs = useAtelierStore((s) => s.jobs);
  const atelierWorkers = useAtelierStore((s) => s.workers);
  const fetchAtelierData = useAtelierStore((s) => s.fetchData);

  useEffect(() => {
    if (orgData?.id) {
      fetchOrdres(orgData.id);
      fetchAtelierData(orgData.id);
    }
  }, [orgData?.id]);

  const ratedOrdres = useMemo(
    () => ordres.filter((o) => o.client_rating != null && o.client_rating > 0),
    [ordres],
  );

  const filtered = useMemo(() => {
    if (filter === "positive") return ratedOrdres.filter((o) => (o.client_rating || 0) >= 4);
    if (filter === "negative") return ratedOrdres.filter((o) => (o.client_rating || 0) <= 2);
    return ratedOrdres;
  }, [ratedOrdres, filter]);

  // Stats
  const stats = useMemo(() => {
    if (ratedOrdres.length === 0) return null;
    const avg = ratedOrdres.reduce((sum, o) => sum + (o.client_rating || 0), 0) / ratedOrdres.length;
    const dist = [0, 0, 0, 0, 0];
    ratedOrdres.forEach((o) => { if (o.client_rating) dist[o.client_rating - 1]++; });
    return { avg: Math.round(avg * 10) / 10, total: ratedOrdres.length, dist };
  }, [ratedOrdres]);

  // Per-member stats (assigned_to on ordre)
  const memberStats = useMemo(() => {
    const map = new Map<string, { name: string; total: number; sum: number }>();
    ratedOrdres.forEach((o) => {
      const memberId = o.assigned_to;
      if (!memberId) return;
      if (!map.has(memberId)) {
        const member = orgMembers.find((m) => m.id === memberId);
        const name = member ? (member.infos.display_name || member.infos.full_name || memberId) : memberId;
        map.set(memberId, { name, total: 0, sum: 0 });
      }
      const entry = map.get(memberId)!;
      entry.total++;
      entry.sum += o.client_rating || 0;
    });
    return Array.from(map.values())
      .map((w) => ({ ...w, avg: Math.round((w.sum / w.total) * 10) / 10 }))
      .sort((a, b) => b.avg - a.avg);
  }, [ratedOrdres, orgMembers]);

  // Per-technician stats (worker from atelier jobs linked to rated ordres)
  const technicianStats = useMemo(() => {
    // Build ordre_id → worker_id mapping from atelier jobs
    const ordreToWorkers = new Map<string, Set<string>>();
    atelierJobs.forEach((j) => {
      if (!j.ordre_id || !j.workerId) return;
      if (!ordreToWorkers.has(j.ordre_id)) ordreToWorkers.set(j.ordre_id, new Set());
      ordreToWorkers.get(j.ordre_id)!.add(j.workerId);
    });

    const map = new Map<string, { name: string; total: number; sum: number }>();
    ratedOrdres.forEach((o) => {
      const workerIds = ordreToWorkers.get(o.id);
      if (!workerIds) return;
      workerIds.forEach((wId) => {
        if (!map.has(wId)) {
          const worker = atelierWorkers.find((w) => w.id === wId);
          const name = worker?.name || wId;
          map.set(wId, { name, total: 0, sum: 0 });
        }
        const entry = map.get(wId)!;
        entry.total++;
        entry.sum += o.client_rating || 0;
      });
    });
    return Array.from(map.values())
      .map((w) => ({ ...w, avg: Math.round((w.sum / w.total) * 10) / 10 }))
      .sort((a, b) => b.avg - a.avg);
  }, [ratedOrdres, atelierJobs, atelierWorkers]);

  const TABS: { key: FilterTab; label: string }[] = [
    { key: "all", label: "Tous" },
    { key: "positive", label: "Positifs" },
    { key: "negative", label: "Négatifs" },
  ];

  return (
    <div className="flex flex-col gap-6 p-8 max-w-800">
      <div className="flex flex-col gap-1">
        <span className="text-xl font-semibold text-black">Avis clients</span>
        <span className="text-h3 text-neutral-400">Notes laissées par vos clients après restitution</span>
      </div>

      {!stats ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="#E5E5E5">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          <span className="text-h2 text-neutral-300">Aucun avis pour le moment</span>
        </div>
      ) : (
        <>
          {/* Overview */}
          <div className="flex flex-row gap-4">
            {/* Score card */}
            <div className="flex flex-col items-center justify-center gap-2 bg-white border rounded-xl p-6 shadow-sm min-w-160">
              <span className="text-4xl font-bold text-black">{stats.avg}</span>
              <Stars rating={Math.round(stats.avg)} size={16} />
              <span className="text-h3 text-neutral-400">{stats.total} avis</span>
            </div>

            {/* Distribution */}
            <div className="flex flex-col flex-1 bg-white border rounded-xl p-4 gap-2 shadow-sm justify-center">
              {[5, 4, 3, 2, 1].map((r) => (
                <RatingBar key={r} label={RATING_LABELS[r]} count={stats.dist[r - 1]} total={stats.total} rating={r} />
              ))}
            </div>
          </div>

          {/* Per-member (assigned_to) */}
          {memberStats.length > 0 && (
            <div className="flex flex-col bg-white border rounded-xl p-4 gap-3 shadow-sm">
              <span className="font-semibold text-black">Par membre</span>
              <div className="flex flex-col divide-y">
                {memberStats.map((w) => (
                  <div key={w.name} className="flex flex-row items-center justify-between py-2.5">
                    <div className="flex flex-row items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                        <span className="text-h3 font-semibold text-neutral-500">{w.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-h2 font-medium text-black">{w.name}</span>
                        <span className="text-h4 text-neutral-400">{w.total} avis</span>
                      </div>
                    </div>
                    <div className="flex flex-row items-center gap-2">
                      <Stars rating={Math.round(w.avg)} size={10} />
                      <span className="text-h2 font-semibold" style={{ color: w.avg >= 4 ? "#22C55E" : w.avg >= 3 ? "#F59E0B" : "#EF4444" }}>{w.avg}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-technician (atelier worker) */}
          {technicianStats.length > 0 && (
            <div className="flex flex-col bg-white border rounded-xl p-4 gap-3 shadow-sm">
              <span className="font-semibold text-black">Par technicien</span>
              <div className="flex flex-col divide-y">
                {technicianStats.map((w) => (
                  <div key={w.name} className="flex flex-row items-center justify-between py-2.5">
                    <div className="flex flex-row items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                        <span className="text-h3 font-semibold text-neutral-500">{w.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-h2 font-medium text-black">{w.name}</span>
                        <span className="text-h4 text-neutral-400">{w.total} avis</span>
                      </div>
                    </div>
                    <div className="flex flex-row items-center gap-2">
                      <Stars rating={Math.round(w.avg)} size={10} />
                      <span className="text-h2 font-semibold" style={{ color: w.avg >= 4 ? "#22C55E" : w.avg >= 3 ? "#F59E0B" : "#EF4444" }}>{w.avg}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filter tabs + list */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-row gap-1">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  className="px-4 py-1.5 rounded-full text-h3 font-medium transition-colors"
                  style={{
                    background: filter === tab.key ? "#000" : "#F5F5F5",
                    color: filter === tab.key ? "#FFF" : "#A1A1A1",
                  }}
                  onClick={() => setFilter(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col bg-white border rounded-xl shadow-sm divide-y">
              {filtered.length === 0 && (
                <div className="text-h3 text-neutral-300 py-6 text-center">Aucun avis dans cette catégorie</div>
              )}
              {filtered.map((ordre) => (
                <AvisRow key={ordre.id} ordre={ordre} orgMembers={orgMembers} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AvisRow({ ordre, orgMembers }: { ordre: Ordre; orgMembers: any[] }) {
  const worker = orgMembers.find((m) => m.id === ordre.assigned_to);
  const workerName = worker ? (worker.infos.display_name || worker.infos.full_name) : null;
  const rating = ordre.client_rating || 0;

  return (
    <div
      className="flex flex-row items-center gap-4 px-4 py-3 hover:bg-neutral-50 cursor-pointer transition-colors"
      onClick={() => navigateTo({ route: `/ordre/${ordre.id}` })}
    >
      <div className="flex flex-col flex-1 min-w-0 gap-0.5">
        <div className="flex flex-row items-center gap-2">
          <span className="text-h2 font-medium text-black">{ordre.name}</span>
          <span className="text-h4 text-neutral-300">{ordre.num}</span>
        </div>
        <div className="flex flex-row items-center gap-2">
          {ordre.immatriculation && <span className="text-h3 text-neutral-400">{ordre.immatriculation}</span>}
          {workerName && (
            <>
              <span className="text-h4 text-neutral-300">·</span>
              <span className="text-h3 text-neutral-400">{workerName}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        <Stars rating={rating} size={12} />
        <span className="text-h4" style={{ color: rating >= 4 ? "#22C55E" : rating >= 3 ? "#F59E0B" : "#EF4444" }}>
          {RATING_LABELS[rating]}
        </span>
      </div>
    </div>
  );
}
